'use client';

import { useEffect, useMemo, useState } from 'react';
import { c } from '../../../utils/tailwind-merge';
import { Dropdown, DropdownOption } from '../../atoms/Dropdown/Dropdown';
import { Separator } from '../../atoms/Separator/Separator';
import { RichText } from '../../molecules/RichText/RichText';
import { MyAssetDetail } from '../MyAssetDetail/MyAssetDetail';
import {
  FILTER_BY_RESULT_DEFAULT,
  FilterByResult,
  MyAssetsListProps,
  SORT_BY_DEFAULT,
  SortByOption,
} from './MyAssetsList.types';

const SortByDropdown = ({
  microcopy,
  onSortBy,
}: Pick<MyAssetsListProps, 'microcopy' | 'onSortBy'>) => {
  const [sortByPurchasedDateMicrocopy, setSortByPurchasedDateMicrocopy] =
    useState<string>(microcopy.sortByPurchasedDate);
  const [sortByNameAscMicrocopy, setSortByNameAscMicrocopy] = useState<string>(
    microcopy.sortByNameAsc
  );
  const [sortByNameDescMicrocopy, setSortByNameDescMicrocopy] =
    useState<string>(microcopy.sortByNameDesc);

  const sortByOptions = useMemo(
    () => [
      {
        value: SortByOption.PURCHASED_DATE,
        label: sortByPurchasedDateMicrocopy,
      },
      {
        value: SortByOption.NAME_ASC,
        label: sortByNameAscMicrocopy,
      },
      {
        value: SortByOption.NAME_DESC,
        label: sortByNameDescMicrocopy,
      },
    ],
    [
      sortByPurchasedDateMicrocopy,
      sortByNameAscMicrocopy,
      sortByNameDescMicrocopy,
    ]
  );

  const [selectedSortBy, setSelectedSortBy] = useState<DropdownOption>(
    sortByOptions.find((option) => option.value === SORT_BY_DEFAULT) ||
      sortByOptions[0]
  );

  useEffect(() => {
    setSortByPurchasedDateMicrocopy(microcopy.sortByPurchasedDate);
    setSortByNameAscMicrocopy(microcopy.sortByNameAsc);
    setSortByNameDescMicrocopy(microcopy.sortByNameDesc);
  }, [
    microcopy.sortByPurchasedDate,
    microcopy.sortByNameAsc,
    microcopy.sortByNameDesc,
  ]);

  return (
    <Dropdown
      id="my-assets-sort-by-dropdown"
      options={sortByOptions}
      selectedOption={selectedSortBy}
      onSelect={(opt) => {
        setSelectedSortBy(opt);
        onSortBy(opt.value as SortByOption);
      }}
      isDarkTheme={false}
      className="lg:min-w-36 lg:max-w-fit"
    />
  );
};

const ResultsDropdown = ({
  onFilterByResult,
}: Pick<MyAssetsListProps, 'onFilterByResult'>) => {
  const sortByOptions: DropdownOption[] = [
    {
      value: String(FilterByResult.SMALL),
      label: String(FilterByResult.SMALL),
    },
    {
      value: String(FilterByResult.MEDIUM),
      label: String(FilterByResult.MEDIUM),
    },
    {
      value: String(FilterByResult.LARGE),
      label: String(FilterByResult.LARGE),
    },
  ];

  const [selectedSortBy, setSelectedSortBy] = useState<DropdownOption>(
    sortByOptions.find(
      (option) => option.value === String(FILTER_BY_RESULT_DEFAULT)
    ) || sortByOptions[0]
  );

  return (
    <Dropdown
      id="my-assets-results-dropdown"
      options={sortByOptions}
      selectedOption={selectedSortBy}
      onSelect={(opt) => {
        setSelectedSortBy(opt);
        onFilterByResult(Number(opt.value) as FilterByResult);
      }}
      isDarkTheme={false}
      className="w-fit min-w-20"
    />
  );
};

export const MyAssetsList = ({
  results,
  assets,
  categories,
  selectedCategory,
  microcopy,
  className = '',
  variant = 'default',
  onSortBy,
  onFilterByResult,
  onFilterByCategory,
  onOpenInUnity,
  onSeeOwnedPackages,
}: MyAssetsListProps) => {
  return (
    <div
      data-test="upm-packages-section"
      className={c('flex flex-col gap-11', className)}
    >
      <h1 className="hidden text-5xl font-bold text-grey-900 lg:block">
        {microcopy.title}
      </h1>
      <div className="flex gap-2">
        <nav className="hidden w-72 lg:block">
          <div className="pb-5">
            <span className="text-sm font-semibold uppercase tracking-wide text-grey-700">
              {microcopy.categories}
            </span>
          </div>
          <div className="flex flex-col items-start">
            {categories.map((category) => (
              <button
                key={category.label}
                aria-label={category.label}
                className="w-full space-x-1 py-2 text-left hover:bg-grey-100"
                onClick={() =>
                  onFilterByCategory(
                    selectedCategory === category.label ? '' : category.label
                  )
                }
              >
                <span
                  className={c(
                    'text-base text-grey-900',
                    selectedCategory === category.label && 'font-bold'
                  )}
                >
                  {category.label}
                </span>
                <span className="text-base text-grey-600">
                  ({category.count})
                </span>
              </button>
            ))}
          </div>
        </nav>

        <div className="flex w-full flex-col gap-6">
          <div className="flex justify-between gap-2">
            <div className="w-full space-y-2">
              <div className="text-sm text-grey-600">{microcopy.sortBy}</div>
              {SortByDropdown({ microcopy, onSortBy })}
            </div>
            <div className="shrink-0 space-y-2">
              <div className="text-sm text-grey-600">
                {microcopy.resultsPerPage}
              </div>
              {ResultsDropdown({ onFilterByResult })}
            </div>
          </div>
          <div>
            <div>
              <div className="flex">
                <RichText
                  html={results}
                  className="text-sm text-grey-600 lg:w-[422px]"
                />
                {variant === 'show-update' && (
                  <div className="hidden text-sm text-grey-600 lg:block">
                    {microcopy.update}
                  </div>
                )}
              </div>
              <Separator />
              {assets.length > 0 ? (
                assets.map((asset) => (
                  <div key={asset.productId}>
                    <MyAssetDetail
                      {...asset}
                      microcopy={microcopy.detail}
                      onClick={() => {
                        if (asset.variant === 'single-package') {
                          onOpenInUnity(asset);
                        } else {
                          onSeeOwnedPackages(asset);
                        }
                      }}
                    />
                    <Separator />
                  </div>
                ))
              ) : (
                <div className="text-sm text-grey-700">
                  {microcopy.noResults}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
